import { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import {
  useCreateEmployeeProfileBlock,
  useDeleteEmployeeProfileBlock,
  useEmployeeProfileBlocks,
  useRenameEmployeeProfileBlock,
  useReorderEmployeeProfileBlocks,
} from '@/hooks/use-employee-profile-blocks';
import { useEmployeeProfileFieldValues } from '@/hooks/use-employee-profile-block-fields';
import { usePermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type {
  EmployeeProfileBlock,
  EmployeeProfileFieldValue,
  EmployeeProfileSection,
} from '@hygge/shared';
import { BlockNameDialog } from './block-name-dialog';
import { CustomBlockCard } from './custom-block-card';
import { DeleteBlockDialog } from './delete-block-dialog';

interface CustomBlocksSectionProps {
  section: EmployeeProfileSection;
  employeeId: string;
  className?: string;
}

function extractApiError(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string | string[] }> | undefined;
  const data = axiosError?.response?.data;
  if (data?.message) {
    return Array.isArray(data.message) ? data.message.join(', ') : data.message;
  }
  return fallback;
}

export function CustomBlocksSection({
  section,
  employeeId,
  className,
}: CustomBlocksSectionProps) {
  const canCustomize = usePermission('employees.profile.customize');
  const canEditValue = usePermission('employees.profile.edit');
  const { data: blocks, isLoading } = useEmployeeProfileBlocks(section);
  const { data: values } = useEmployeeProfileFieldValues(employeeId);
  const valueMap = useMemo(() => {
    const map = new Map<string, EmployeeProfileFieldValue>();
    for (const v of values ?? []) map.set(v.fieldId, v);
    return map;
  }, [values]);
  const createMutation = useCreateEmployeeProfileBlock();
  const renameMutation = useRenameEmployeeProfileBlock();
  const deleteMutation = useDeleteEmployeeProfileBlock(section);
  const reorderMutation = useReorderEmployeeProfileBlocks();

  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<EmployeeProfileBlock | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeProfileBlock | null>(null);

  const sorted = useMemo(
    () =>
      [...(blocks ?? [])].sort(
        (a, b) =>
          a.position - b.position ||
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [blocks],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = sorted.findIndex((b) => b.id === active.id);
      const newIndex = sorted.findIndex((b) => b.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(sorted, oldIndex, newIndex);
      reorderMutation.mutate(
        { section, blockIds: reordered.map((b) => b.id) },
        {
          onError: (err) => {
            toast.error(extractApiError(err, 'Failed to reorder blocks'));
          },
        },
      );
    },
    [section, sorted, reorderMutation],
  );

  const handleCreate = (name: string) => {
    createMutation.mutate(
      { section, name },
      {
        onSuccess: () => setCreateOpen(false),
        onError: (err) => toast.error(extractApiError(err, 'Failed to create block')),
      },
    );
  };

  const handleRename = (name: string) => {
    if (!renameTarget) return;
    renameMutation.mutate(
      { id: renameTarget.id, name },
      {
        onSuccess: () => setRenameTarget(null),
        onError: (err) => toast.error(extractApiError(err, 'Failed to rename block')),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: (err) => toast.error(extractApiError(err, 'Failed to delete block')),
    });
  };

  if (isLoading) {
    return null;
  }

  const hasBlocks = sorted.length > 0;
  if (!hasBlocks && !canCustomize) {
    return null;
  }

  return (
    <div className={cn('space-y-3', className)}>
      {hasBlocks && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sorted.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {sorted.map((block) => (
                <CustomBlockCard
                  key={block.id}
                  block={block}
                  employeeId={employeeId}
                  values={valueMap}
                  canCustomize={canCustomize}
                  canEditValue={canEditValue}
                  onRename={() => setRenameTarget(block)}
                  onDelete={() => setDeleteTarget(block)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {canCustomize && (
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className={cn(
            'flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border/70',
            'px-4 py-3 text-xs font-medium text-muted-foreground',
            'hover:border-foreground/40 hover:text-foreground hover:bg-muted/40',
            'transition-colors cursor-pointer',
          )}
        >
          <Plus className="h-3.5 w-3.5" />
          Add New Block
        </button>
      )}

      <BlockNameDialog
        open={createOpen}
        mode="create"
        isSubmitting={createMutation.isPending}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
      />

      <BlockNameDialog
        open={renameTarget !== null}
        mode="rename"
        initialName={renameTarget?.name ?? ''}
        isSubmitting={renameMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
        onSubmit={handleRename}
      />

      <DeleteBlockDialog
        open={deleteTarget !== null}
        blockName={deleteTarget?.name ?? ''}
        hasStoredData={deleteTarget?.hasStoredData ?? false}
        isDeleting={deleteMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
}
